import React, { useState, useEffect } from 'react';
import { api, API_ROUTERS } from '../../../services/api';
import UsuarioContent from './view';
import Store, { useAppSelector, useAppDispatch } from '../../../store';
import { Usuario, FormData } from './types';

const UsuarioContainer: React.FC = () => {
  const initialFormData: FormData = {
    nomeUsuario: '',
    cpf: '',
    telefone: '',
    email: '',
    observacao: '',
    login: '',
    codigoGrupoUsuario: 2,
    empresa: '',
  };

  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (user.empresa && user.empresa !== formData.empresa) {
      setFormData((prevData) => ({
        ...prevData,
        empresa: user.empresa,
      }));
    }
  }, [formData.empresa, user.empresa]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    if (name === 'email') {
      setFormData({
        ...formData,
        email: value,
        login: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]:
          name === 'numero' ? (value === '' ? '' : parseInt(value)) : value,
      });
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = async () => {
    setError('');
    setSuccess('');
    setUsuarios([]);
    try {
      Store.load.show(dispatch, 'Consultando usuários');
      const response = await api.get(API_ROUTERS.USUARIO);
      let allUsers: Usuario[] = api.utils.trataResponseArray(
        response
      ) as Usuario[];
      if (!Array.isArray(allUsers) || allUsers.length === 0) {
        setError('Nenhum usuário encontrado');
        setUsuarios([]);
        Store.load.hide(dispatch);
        return;
      }

      if (searchTerm.trim() === '') {
        setUsuarios(allUsers);
        setSuccess('Selecione um usuário da lista');
        Store.load.hide(dispatch);
        return;
      }

      const searchCode = Number(searchTerm);

      if (!isNaN(searchCode)) {
        const userByCode = allUsers.find(
          (user) => user.codigoUsuario === searchCode
        );

        if (userByCode) {
          setUsuarios([userByCode]);
          setSuccess('Usuário encontrado');
        } else {
          setError('Nenhum usuário encontrado com este código');
        }
      } else {
        setError('Por favor, informe um código de usuário válido');
      }
    } catch (err) {
      console.error('Erro na pesquisa:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao buscar usuários');
      }
      setUsuarios([]);
    } finally {
      Store.load.hide(dispatch);
    }
  };

  const handleEdit = (usuario: Usuario) => {
    setFormData({
      nomeUsuario: usuario.nomeUsuario || '',
      cpf: usuario.cpf || '',
      telefone: usuario.telefone || '',
      email: usuario.email || '',
      observacao: usuario.observacao || '',
      login: usuario.login || usuario.email || '',
      codigoGrupoUsuario: usuario.codigoGrupoUsuario || 2,
      empresa: user.empresa || '',
    });

    setSuccess(
      'Usuário carregado para edição. Navegue pelas abas para ver todos os dados.'
    );

    // Atualiza o campo de pesquisa com o código do usuário
    setSearchTerm(usuario.codigoUsuario.toString());
    setUsuarios([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!formData.nomeUsuario || !formData.cpf || !formData.email) {
        throw new Error('Por favor, preencha todos os campos obrigatórios');
      }

      if (!formData.empresa) {
        throw new Error(
          'Empresa não identificada. Por favor, faça login novamente.'
        );
      }
      Store.load.show(dispatch, 'Salvando usuário');

      const dataToSend = {
        nomeUsuario: formData.nomeUsuario,
        cpf: formData.cpf,
        telefone: formData.telefone,
        email: formData.email,
        observacao: formData.observacao,
        login: formData.email,
        codigoGrupoUsuario: formData.codigoGrupoUsuario,
        empresa: formData.empresa,
      };

      const response = await api.post(API_ROUTERS.USUARIO, dataToSend);
      if (usuarios.length > 0 && response.data && response.data.codigoUsuario) {
        setUsuarios((prevUsuarios) => [
          ...prevUsuarios,
          {
            ...dataToSend,
            codigoUsuario: response.data.codigoUsuario,
          } as Usuario,
        ]);
      }

      Store.dialog.alert(dispatch, 'Usuário salvo com sucesso!', 'success');

      const empresa = user.empresa || '';
      setFormData({
        ...initialFormData,
        empresa
      });
      setSearchTerm('');
    } catch (err) {
      if (err instanceof Error) {
        Store.dialog.alert(dispatch, err.message, 'error');
      } else {
        Store.dialog.alert(dispatch, 'Ocorreu um erro ao processar a solicitação', 'error');
      }
    } finally {
      Store.load.hide(dispatch);
    }
  };

  return (
    <UsuarioContent
      formData={formData}
      error={error}
      success={success}
      searchTerm={searchTerm}
      usuarios={usuarios}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      handleSearch={handleSearch}
      handleSearchInputChange={handleSearchInputChange}
      handleEdit={handleEdit}
      setUsuarios={setUsuarios}
      setSearchTerm={setSearchTerm}
      setSuccess={setSuccess}
      setFormData={setFormData}
    />
  );
};

export default UsuarioContainer;
